import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { type SetupContext } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { AccessDslConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import VueDatePicker from "../../Shared/LazyDatePicker";
import { de } from "date-fns/locale";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}
export default defineComponent({
  template: html,
  components: { VueDatePicker },
  emits: {
    next: () => true,
  },
  setup(_props: any, { emit }: SetupContext) {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const currentOrderProvider = inject(currentOrderProviderKey);

    // vars und computed

    const date = ref<null | Date>(null);
    const config = computed(() => {
      return (
        currentOrderProvider?.currentOrder?.value?.configs.find(
          (c) => c.type == "AccessDslConfig" || c.type == "AccessFiberConfig",
        ) ?? null
      );
    });
    const first_activation_date = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.access_context
          ?.first_activation_date,
    );
    const wishdate = ref<string | null>(null);
    const wishdateChoiceSelection = ref<string>("asap");
    const wishdateError = ref("");
    const wishdateOffsetDays = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.tenant_context
          ?.wishdate_offset_days,
    );
    const wishdateExcluded = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.tenant_context
          ?.wishdate_excluded ?? [],
    );

    const wishdateChoices = computed(() => {
      let choices = [
        {
          key: "asap",
          text: t("ConfigsAccess_Wishdate_Asap"),
        },
      ];
      if (first_activation_date.value != null) {
        choices.push({
          key: "first_activation_date",
          text: `Zum Erschliessungstermin am ${first_activation_date.value.split("-").reverse().join(".")}  (möglich falls Termin mindestens 20 Arbeitstage in der Zukunft liegt)`,
        });
      }
      choices.push({
        key: "wishdate",
        text: t("ConfigsAccess_Wishdate_Wishdate"), 
      });
      return choices;
    });

    // methods
    const next = () => {
      emit("next");
    };

    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        const conf = config.value as any;
        if (conf == null || orderService == null) {
          return;
        }
        if (conf.type == "AccessDslConfig") {
          const request: AccessDslConfigPatchRequest = {
            wishdate: wishdate.value,
            wishdate_type: wishdateChoiceSelection.value,
          };

          await orderService.patchConfig(conf["uuid"], conf.type, request);
        }
      };
      pendingSave = run();
      await pendingSave;
    };
    const onUpdateWishdate = async (evt: Date | null) => {
      if (evt != null) {
        const yyyy = evt.getFullYear();
        const mm = String(evt.getMonth() + 1).padStart(2, "0");
        const dd = String(evt.getDate()).padStart(2, "0");
        wishdate.value = `${yyyy}-${mm}-${dd}`;
        wishdateChoiceSelection.value = "wishdate";
        wishdateError.value = "";
        await save();
      }
    };

    const onWishdateChoiceChange = async (_evt: Event) => {
      wishdateError.value = "";
      if (wishdateChoiceSelection.value === "asap") {
        wishdate.value = null;
        await save();
      } else if (wishdateChoiceSelection.value === "first_activation_date") {
        wishdate.value = first_activation_date.value ?? null;
        await save();
      }
    };

    const isValid = computed(() => {
      if (wishdateChoiceSelection.value === "wishdate" && !wishdate.value) {
        return false;
      }
      return !wishdateError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (wishdateChoiceSelection.value === "wishdate" && !wishdate.value) {
        wishdateError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    const disabledDates = function (dt: Date) {
      // dt is Date such as Fri, Aug 01 2025 00:00:00 GMT+0200
      // Bei allen Vergleichsdaten sicherstellen, dass auch die Zeit 00:00:00 gesetzt wird.

      //let mandant = null; // todo this.main_store.mandant;

      let limit_days = wishdateOffsetDays.value ?? 0;
      let earliest = new Date();
      earliest.setHours(0, 0, 0, 0);
      earliest.setDate(earliest.getDate() + limit_days);
      if (dt < earliest) {
        return true;
      }
      let fad = first_activation_date.value;
      if (fad) {
        let earliest_fad = new Date(fad);
        earliest_fad.setHours(0, 0, 0, 0);
        if (dt < earliest_fad) {
          return true;
        }
      }
      const excluded = wishdateExcluded.value;
      if (Array.isArray(excluded)) {
        // Achtung: Mandant-Config verwendet ISO_8601
        // Tag | ISO_8601 | Javascript
        // Mo  | 1        | 1
        // Di  | 2        | 2
        // Mi  | 3        | 3
        // Do  | 4        | 4
        // Fr  | 5        | 5
        // Sa  | 6        | 6
        // So  | 7        | 0
        for (var i = 0; i < excluded.length; i++) {
          let exclusion = excluded[i].trim();
          const match_day = /^D(\d+)$/.exec(exclusion);
          if (match_day) {
            let excluded_day = parseInt(match_day[1], 10);
            if (7 == excluded_day) {
              excluded_day = 0; // Javascript ist 0-basiert
            }
            if (dt.getDay() == excluded_day) {
              return true;
            }
          }
          // Ausschluss bestimmter Daten
          const match_date = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.exec(exclusion);
          if (match_date) {
            let excluded_date = new Date(match_date[0]);
            excluded_date.setHours(0, 0, 0, 0);
            if (dt.toISOString() == excluded_date.toISOString()) {
              return true;
            }
          }
          // Ausschluss bestimmter Daten (wiederholt)
          const match_date_every_year = /^--[0-9]{2}-[0-9]{2}$/.exec(exclusion);
          if (match_date_every_year) {
            let today = new Date();
            let date_str = match_date_every_year[0].replace(
              "--",
              `${today.getFullYear()}-`,
            );
            let excluded_date = new Date(date_str);
            excluded_date.setHours(0, 0, 0, 0);
            if (
              dt.getMonth() == excluded_date.getMonth() &&
              dt.getDate() == excluded_date.getDate()
            ) {
              return true;
            }
          }
        }
      }

      return false;
    };

    onMounted(function () {
      const conf = config.value as any;
      if (conf == null) {
        return;
      }

      wishdateChoiceSelection.value = conf["data"]["wishdate_type"];
      date.value =
        null == conf["data"]["wishdate"]
          ? null
          : new Date(conf["data"]["wishdate"]);
    });

    return {
      t,
      date,
      de,
      disabledDates,
      wishdateChoices,
      wishdateChoiceSelection,
      wishdateError,
      onWishdateChoiceChange,
      onUpdateWishdate,
      next,
      save,
      isValid,
      validate,
    };
  },
} as any);
